import axios from 'axios';
import { store } from '../module/store';
import { clearAuthenticationState } from '../module/features/Authentication';
import { cookieManager } from '../Constant/Utils';

export const SESSION_EXPIRED_FLAG = 'sp-session-expired';
export const SESSION_EXPIRED_QUERY = 'sessionExpired';

const SESSION_EXPIRED_CODES = ['session_expired', 'session_invalid'];
const LEGACY_SESSION_INVALID_MESSAGE = 'Invalid or expired session token';
const SESSION_STORAGE_KEYS = ['access_token', 'auth0-user', 'userMetadata', 'adminRole'];
// setTimeout overflows above ~24.8 days.
const MAX_TIMEOUT_MS = 2147483647;

let handlingExpiry = false;
let interceptorInstalled = false;
let expiryTimerId = null;

export const getSessionTokenExpiryMs = token => {
  try {
    const encodedPayload = token?.split('.')?.[1];
    if (!encodedPayload) {
      return null;
    }
    const base64 = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')));
    return Number.isFinite(payload?.exp) ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

export const isSessionExpiredPayload = (status, data) =>
  status === 401 &&
  (SESSION_EXPIRED_CODES.includes(data?.code) || data?.message === LEGACY_SESSION_INVALID_MESSAGE);

const isOwnBackendUrl = url => {
  const backendUrl = process.env.REACT_APP_BACKEND_URI;
  return Boolean(backendUrl && typeof url === 'string' && url.startsWith(backendUrl));
};

export const isSessionExpiredResponse = error =>
  isOwnBackendUrl(error?.config?.url) &&
  isSessionExpiredPayload(error?.response?.status, error?.response?.data);

export const handleSessionExpired = () => {
  if (handlingExpiry) {
    return;
  }
  handlingExpiry = true;

  if (expiryTimerId) {
    window.clearTimeout(expiryTimerId);
    expiryTimerId = null;
  }

  SESSION_STORAGE_KEYS.forEach(key => localStorage.removeItem(key));
  cookieManager.deleteCookie('token');
  store.dispatch(clearAuthenticationState());
  sessionStorage.setItem(SESSION_EXPIRED_FLAG, '1');
  window.location.replace(`/?${SESSION_EXPIRED_QUERY}=true`);
};

export const installSessionExpiryInterceptor = (axiosInstance = axios) => {
  if (interceptorInstalled) {
    return;
  }
  interceptorInstalled = true;

  axiosInstance.interceptors.response.use(
    response => response,
    error => {
      if (isSessionExpiredResponse(error)) {
        handleSessionExpired();
      }
      return Promise.reject(error);
    }
  );
};

export const scheduleSessionExpiryLogout = () => {
  if (expiryTimerId) {
    window.clearTimeout(expiryTimerId);
    expiryTimerId = null;
  }

  const expiresAtMs = getSessionTokenExpiryMs(localStorage.getItem('access_token'));
  if (!expiresAtMs) {
    return;
  }

  const remainingMs = expiresAtMs - Date.now();
  if (remainingMs <= 0) {
    handleSessionExpired();
    return;
  }

  expiryTimerId = window.setTimeout(
    scheduleSessionExpiryLogout,
    Math.min(remainingMs, MAX_TIMEOUT_MS)
  );
};

export const watchSessionExpiry = () => {
  installSessionExpiryInterceptor();
  scheduleSessionExpiryLogout();

  // Another tab logging in or out changes the shared token.
  window.addEventListener('storage', event => {
    if (event.key === 'access_token') {
      scheduleSessionExpiryLogout();
    }
  });
};
